import React, { useEffect, useRef, useState } from 'react';
import { Modal, Alert, Empty, Typography, Tag, message } from 'antd';
import { WhatsAppOutlined, SendOutlined, TrophyOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;

function fmtCooldown(sec) {
  if (sec <= 0) return 'agora';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

const GameStatsBroadcastModal = ({ open, gameId, gameLabel, onClose, onSent }) => {
  const [previewText, setPreviewText] = useState('');
  const [hasData, setHasData] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ can_send: true, wait_seconds: 0, already_sent: false });
  const countdownRef = useRef(null);

  useEffect(() => {
    if (!open || !gameId) return;
    setPreviewLoading(true);
    setPreviewText('');
    // Puxa preview + status em paralelo
    Promise.all([
      api.post(`/organizer-games/${gameId}/stats-broadcast`, { preview: true }),
      api.get(`/organizer-games/${gameId}/stats-broadcast-status`).catch(() => ({ data: { can_send: true } })),
    ]).then(([p, s]) => {
      setPreviewText(p.data?.text || '');
      setHasData(!!p.data?.has_data);
      setStatus(s.data || { can_send: true });
    }).catch(err => {
      message.error(err?.response?.data?.mensagem || err?.response?.data?.error || 'Erro ao gerar preview');
    }).finally(() => setPreviewLoading(false));
  }, [open, gameId]);

  // Countdown local do cooldown
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (status.can_send || status.already_sent || status.wait_seconds <= 0) return;
    countdownRef.current = setInterval(() => {
      setStatus(prev => {
        const s = Math.max(0, (prev.wait_seconds || 0) - 1);
        if (s === 0) return { ...prev, wait_seconds: 0, can_send: true };
        return { ...prev, wait_seconds: s };
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [status.can_send, status.already_sent, status.wait_seconds]);

  const confirmSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/organizer-games/${gameId}/stats-broadcast`);
      message.success('Ranking enviado no grupo!');
      onSent?.(r.data);
      onClose?.();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'cooldown') {
        setStatus({ can_send: false, wait_seconds: err.response.data.wait_seconds || 21600, already_sent: false });
      } else if (code === 'ja_enviado') {
        setStatus({ can_send: false, already_sent: true });
      }
      message.error(err?.response?.data?.mensagem || 'Erro ao enviar');
    }
    setSending(false);
  };

  const okDisabled = previewLoading || !hasData || !status.can_send;
  const okText = sending
    ? 'Enviando...'
    : (status.already_sent
        ? 'Ranking já enviado'
        : (!status.can_send && status.wait_seconds > 0
            ? `Aguarde ${fmtCooldown(status.wait_seconds)}`
            : 'Enviar agora'));

  return (
    <Modal
      title={
        <span><TrophyOutlined style={{ color: '#faad14' }} /> Ranking do jogo — <Tag>{gameLabel || `Jogo #${gameId}`}</Tag></span>
      }
      open={open}
      onCancel={onClose}
      onOk={confirmSend}
      confirmLoading={sending}
      cancelText="Cancelar"
      okText={okText}
      okButtonProps={{
        disabled: okDisabled,
        icon: <WhatsAppOutlined />,
        style: !okDisabled ? { background: '#25D366', borderColor: '#25D366', fontWeight: 600 } : undefined,
      }}
      width={560}
    >
      {status.already_sent ? (
        <Alert type="info" showIcon
          message="Ranking desse jogo já foi enviado"
          description="Cada jogo pode ter seu ranking enviado no grupo apenas uma vez."
          style={{ marginBottom: 12, borderRadius: 10 }}
        />
      ) : (
        <Alert type="warning" showIcon
          message="Cooldown de 6 horas"
          description="Depois de enviar, você só poderá enviar outro ranking de jogo daqui 6 horas."
          style={{ marginBottom: 12, borderRadius: 10 }}
        />
      )}
      {previewLoading ? (
        <Empty description="Gerando preview…" />
      ) : !hasData ? (
        <Empty description="Sem estatísticas lançadas nesse jogo (gols/assists). Lance antes de enviar." />
      ) : (
        <div style={{
          background: '#e8f5e9', border: '1px solid #b7eb8f', borderRadius: 12,
          padding: 16, fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap',
          maxHeight: 400, overflowY: 'auto',
        }}>
          {previewText}
        </div>
      )}
    </Modal>
  );
};

export default GameStatsBroadcastModal;
